import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppService } from 'src/app/services/app.service';
import { ReportsService } from '../reports.service';
import { ICaseBalance, ICasePeriodBalance, ICustomerCaseBalanceRequest, ICustomerCaseBalanceResponse, IDebtorCasePeriodBalance } from '../reports-models';
import { ICaseModel, IPaginatedSearch } from 'src/app/shared/models';
import { finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { LocalizationService, LocalizedNumberFormat } from 'src/app/shared/localization/localization.service';
import { LazyLoadEvent } from 'primeng/api';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';
import { Observable, Subject } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { KEYS, MessageToUiService } from 'src/app/services/message-to-ui.service';
import { saveAs } from 'file-saver';
import { CommonModule, KeyValue } from '@angular/common';
import { Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { CustomMultiSelectComponent } from 'src/app/shared/custom-multi-select/custom-multi-select.component';
import { DatePickerModule } from 'primeng/datepicker';
import { SettingsService } from "src/app/services/settings.service";

@Component({
  selector: 'app-outstanding-amount',
  templateUrl: './outstanding-amount.component.html',
  styleUrls: ['./outstanding-amount.component.scss'],
  imports: [CommonModule, TranslateModule, TableModule, MatProgressSpinner, FormsModule, CustomMultiSelectComponent, DatePickerModule]
})
export class OutstandingAmountComponent implements OnInit, OnDestroy {

  constructor(
    private application: AppService,
    private reportsService: ReportsService,
    private localizationService: LocalizationService,
    private spinner: GlobalSpinnerService,
    private translateService: TranslateService,
    private messageToUi: MessageToUiService,
    private settingsService: SettingsService,
    private router: Router
  ) { }
  numberOfPeriodsToLookBack = 2;
  filter = { numberOfPeriodsToLookBack: this.numberOfPeriodsToLookBack } as ICustomerCaseBalanceRequest;
  totalNumberOfItems: number;
  period: Date;
  userAssignedCustomersCount: number;
  selectedCustomers: number[] = [];

  customers$: Observable<KeyValue<number, string>[]> = this.application.customersSelectItems$
    .pipe(tap(_ => {
      this.userAssignedCustomersCount = _.length;
      if (this.userAssignedCustomersCount == 1)
        {this.selectedCustomers = [_[0].key]; }
    }));

  numberFormat: LocalizedNumberFormat;
  dateFormat: string;
  pageNumber = 1;
  numberOfItemsPerPage = 10;
  isLoading = false;
  isDownloading: boolean;
  debtorCasePeriodsBalance: IDebtorCasePeriodBalance[] = [];
  periodsBalanceTotals: ICaseBalance;
  private language$ = this.localizationService.settings$.pipe(map(_ => _.language.toLocaleLowerCase()));
  private readonly unsubscribe$ = new Subject();

  columns = [
    { field: 'caseNumber', translationTag: 'Reports.OutstandingAmount.CaseNumber' },
    { field: 'customerRef1', translationTag: 'Reports.OutstandingAmount.CustomerRef' },
    { field: 'customerRef2', translationTag: 'Reports.OutstandingAmount.CustomerReference2' },
    { field: 'customerRef3', translationTag: 'Reports.Columns.CustomerRef3' },
    { field: 'customerNumber', translationTag: 'Reports.OutstandingAmount.CustomerNumber' },
    { field: 'registeredDate', translationTag: 'Reports.OutstandingAmount.RegisteredDate', type: 'date' },
    { field: 'outstandingAmount', translationTag: 'Reports.OutstandingAmount.TotalOutstandingAmount', type: 'decimal' },
    { field: 'balanceCapital', translationTag: 'Reports.OutstandingAmount.BalanceCapital', type: 'decimal' }
  ];

  periodColumns = [];

  ngOnInit(): void {
    this.localizationService.settings$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(_ => {
        this.numberFormat = _.numberPipeFormat;
        this.dateFormat = _.datePipeFormat;
      });

    this.settingsService.getSettingValueByName('AreRef2Ref3Inlcuded')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((areRef2Ref3Inlcuded) => {
        if (!areRef2Ref3Inlcuded.isTruthy()) {
          this.columns = this.columns.filter(
            col => col.field !== 'customerRef3'
          );
        }
      });

    this.setDefaultPeriod();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  onGenerateReport(event = null) {
    if (this.getFilter()) {
      if (event) {
        this.filter.calculateTotals = true;
        this.spinner.show();
      } else {
        this.isLoading = true;
      }
      const request = {
        currentPage: this.pageNumber,
        pageSize: this.numberOfItemsPerPage,
        sortAscending: true,
        filter: this.filter
      } as IPaginatedSearch<ICustomerCaseBalanceRequest>;
      this.reportsService.getOutstandingAmountsData(request)
        .pipe(takeUntil(this.unsubscribe$))
        .pipe(finalize(() => {
          this.spinner.hide();
          this.filter.calculateTotals = false;
          this.isLoading = false;
        }))
        .subscribe({
          next: (_) => {
            if (_.totalNumberOfItems == 0) {
              this.messageToUi.errorKey(KEYS.Error_NoDataFound);
              return;
            }
            this.totalNumberOfItems = _.totalNumberOfItems;

            if (this.filter.calculateTotals) {this.setTotals(_); }

            this.mapPageItems(_.pageItems);
            this.calculateDebtorTotals();
            this.getPeriodColumns(_.pageItems);
          },
          error: () => this.messageToUi.genericError()
        });
    }
  }

  onDownloadExcel() {
    if (this.getFilter()) {
      this.isDownloading = true;
      this.language$.pipe(switchMap(language =>
        this.reportsService.getOutstandingAmountsExcel(this.filter, language).pipe(finalize(() => this.isDownloading = false))))
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (blob) => saveAs(blob, `${this.translateService.instant('Reports.OutstandingAmount.Title')}_${new Date().getDate() + (new Date().getMonth() + 1) + new Date().getFullYear() + '_' + new Date().getTime().toString().slice(0, 6)}.xlsx`),
          error: () => this.messageToUi.genericError()
        }
        );
    }
  }

  onPageChange(event: LazyLoadEvent) {
    if (event.rows != this.numberOfItemsPerPage) {this.numberOfItemsPerPage = event.rows; }
    this.pageNumber = (event.first == 0) ? 1 : (event.first / event.rows + 1);
    this.onGenerateReport();
  }

  openCaseInNewTab(caseModel: ICaseModel) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/cases/${caseModel.caseNumber}`])
    );
    window.open(url, '_blank');
  }

  private getFilter(): boolean {
    if (!this.period) {
      this.messageToUi.errorKey('Reports.OutstandingAmount.Errors.NoPeriod');
      return false;
    }
    else if (this.selectedCustomers.length == 0) {
      this.messageToUi.errorKey('Reports.OutstandingAmount.Errors.NoCustomers');
      return false;
    }
    else {
      this.filter = { ...this.filter, month: this.period.getMonth() + 1, year: this.period.getFullYear(), customerNumbers: this.selectedCustomers.join(",") };
      return true;
    }
  }

  private setDefaultPeriod() {
    this.period = new Date();
  }

  private mapPageItems(pageItems: ICaseBalance[]) {
    this.debtorCasePeriodsBalance = [];
    pageItems.forEach(x => {
      const index = this.debtorCasePeriodsBalance.findIndex(y => y.customerRef1 == x.customerRef1);
      if (index > -1) {this.debtorCasePeriodsBalance[index].casesBalance.push(x); }
      else {this.debtorCasePeriodsBalance.push({ customerRef1: x.customerRef1, casesBalance: [x] } as IDebtorCasePeriodBalance)}
    });
  }

  private getPeriodColumns(pageItems: ICaseBalance[]) {
    this.periodColumns = pageItems?.length > 0 ? pageItems[0].periodsBalance.map(x => ({ year: x.year, month: x.month })) : this.periodColumns;
  }

  private calculateDebtorTotals() {
    this.debtorCasePeriodsBalance.forEach(x => {
      x.periodsBalanceTotals = {
        customerRef1: x.customerRef1,
        outstandingAmount: x.casesBalance.map(x => x.outstandingAmount).reduce((a, b) => a + b, 0),
        previousPeriodsBalance: x.casesBalance.map(x => x.previousPeriodsBalance).reduce((a, b) => a + b, 0),
        balanceCapital: x.casesBalance.map(x => x.balanceCapital).reduce((a, b) => a + b, 0),
        periodsBalance: []
      } as ICaseBalance;

      const periodsBalance = x.casesBalance.flatMap(x => x.periodsBalance.sort((a, b) => sortByYearAndMonth(a, b)));

      periodsBalance.forEach(p => {
        const index = x.periodsBalanceTotals.periodsBalance.findIndex(y => y.month == p.month && y.year == p.year);
        if (index > -1) {x.periodsBalanceTotals.periodsBalance[index].balanceAmount += p.balanceAmount; }
        else {x.periodsBalanceTotals.periodsBalance.push({
          year: p.year,
          month: p.month,
          balanceAmount: p.balanceAmount
        } as ICasePeriodBalance); }
      });
    });
  }

  private setTotals(response: ICustomerCaseBalanceResponse) {
    this.periodsBalanceTotals = {
      periodsBalance: response.periodBalanceTotals.sort((a, b) => sortByYearAndMonth(a, b)),
      outstandingAmount: response.outstandingAmountTotal,
      previousPeriodsBalance: response.previousPeriodsBalanceTotal,
      balanceCapital: response.balanceCapitalTotal
    } as ICaseBalance;
  }
}

function sortByYearAndMonth(a: ICasePeriodBalance, b: ICasePeriodBalance): number {
  if (a.year == b.year) {return b.month - a.month; }
  return a.year > b.year ? -1 : 1;
}
